import { ethers, BigNumber } from 'ethers';
import detectEthereumProvider from '@metamask/detect-provider';
import { NFT_MULTISEND_ADDRESS, TOKEN_ADDRESSES } from '../constants';
import zutAbi from '../abis/IERC20.json';
import IERC20Abi from '../abis/IERC20.json';
import IERC721Abi from '../abis/IERC721.json';
import IERC1155Abi from '../abis/IERC1155.json';
import NFTMultisendAbi from '../abis/NFTMultisend.json';
import { TOKEN_TYPE } from '../constants';

export const getWeb3Provider = (provider) => {
  if (provider) {
    return new ethers.providers.Web3Provider(provider);
  } else {
    return null;
  }
};

export const getMetamaskProvider = async () => {
  const provider = await detectEthereumProvider();
  if (provider) {
    return provider;
  } else {
    return null;
  }
};

export const isMetamaskConnected = async (provider) => {
  return provider.isConnected();
};

export const addMetamaskListeners = (
  provider,
  chainChangedCallback,
  messageCallback,
  accountsChangedCallback
) => {
  provider.on('chainChanged', (chainId) => {
    chainChangedCallback(chainId);
  });
  provider.on('message', (message) => {
    messageCallback(message);
  });
  provider.on('accountsChanged', (accounts) => {
    accountsChangedCallback(accounts);
  });
};

export const getAccountSigner = async (web3Provider) => {
  return await web3Provider.getSigner();
};

export const getAccountEthBalance = async (
  web3Provider,
  currentAccountAddress
) => {
  return await web3Provider.getBalance(currentAccountAddress);
};

export const getAccountZutBalance = async (
  web3Provider,
  currentAccountAddress
) => {
  const zutContract = new ethers.Contract(
    TOKEN_ADDRESSES.ZUT,
    zutAbi,
    await web3Provider.getSigner()
  );
  return await zutContract.balanceOf(currentAccountAddress);
};

export const weiToEth = (weiBalance) => {
  return ethers.utils.formatEther(weiBalance);
};

export const ethToWei = (ethBalance) => {
  return ethers.utils.parseEther(ethBalance);
};

export const formatUnits = (weiBalance) => {
  return ethers.utils.formatUnits(weiBalance, 18);
};

export const connectToMetamask = (ethereum) => {
  ethereum.request({ method: 'eth_requestAccounts' });
};

export const getERC20Token = async (web3Provider, address) => {
  let contract = new ethers.Contract(
    address,
    IERC20Abi,
    await web3Provider.getSigner()
  );

  return contract;
};

export const getERC721Token = async (web3Provider, address) => {
  let contract = new ethers.Contract(
    address,
    IERC721Abi,
    await web3Provider.getSigner()
  );

  return contract;
};

export const getERC1155Token = async (web3Provider, address) => {
  let contract = new ethers.Contract(
    address,
    IERC1155Abi,
    await web3Provider.getSigner()
  );

  return contract;
};

export const ERC721TokenVerify = async (
  senderAddress,
  tokenIds,
  tokenContract
) => {
  let errorTokenIds = [];
  for (let i = 0; i < tokenIds.length; i++) {
    try {
      if (
        (await tokenContract.ownerOf(tokenIds[i])).toUpperCase() !==
        senderAddress.toUpperCase()
      ) {
        throw new Error('ERC721 token is not owned by the sender account.');
      }
    } catch (error) {
      console.error(error);
      errorTokenIds.push(tokenIds[i]);
    }
  }

  return errorTokenIds;
};

export const ERC1155TokenVerify = async (
  senderAddress,
  tokenIds,
  tokenAmounts,
  tokenContract
) => {
  let errorTokenIds = [];
  for (let i = 0; i < tokenIds.length; i++) {
    try {
      let tokenBalance = await tokenContract.balanceOf(
        senderAddress,
        tokenIds[i]
      );

      if (parseInt(tokenBalance) < tokenAmounts[i]) {
        throw new Error('ERC1155 token is not owned by the sender account.');
      }
    } catch (error) {
      console.error(error);
      errorTokenIds.push(tokenIds[i]);
    }
  }

  return errorTokenIds;
};

export const isApprovedERC20 = async (tokenContract, ownerAddress) => {
  console.log('hi');
  let approved = await tokenContract.allowance(
    ownerAddress,
    NFT_MULTISEND_ADDRESS
  );
  return approved.gt('0');
};

export const approveERC20 = async (tokenContract, callback) => {
  let tokenTotalSupply = await tokenContract.totalSupply();
  await tokenContract.approve(NFT_MULTISEND_ADDRESS, tokenTotalSupply);
  tokenContract.on('Approval', (from, to, value) => {
    callback();
  });
};

export const approveMultisendContractAsOperator = async (
  tokenType,
  tokenContract,
  callback
) => {
  if (tokenType === TOKEN_TYPE.ERC20) {
    let totalSupply = await tokenContract.totalSupply();
    await tokenContract.approve(NFT_MULTISEND_ADDRESS, totalSupply);
    tokenContract.once('Approval', () => {
      callback();
    });
  } else {
    await tokenContract.setApprovalForAll(NFT_MULTISEND_ADDRESS, true);
    tokenContract.once('ApprovalForAll', () => {
      callback();
    });
  }
};

export const isApprovedMultisendContractAsOperator = async (
  tokenContract,
  ownerAddress
) => {
  let approved = await tokenContract.isApprovedForAll(
    ownerAddress,
    NFT_MULTISEND_ADDRESS
  );
  return approved;
};

export const multiSendERC721ETH = async (
  web3Provider,
  tokenContract,
  receivingAddresses,
  senderAddress,
  tokenIds
) => {
  let NFTMultisendContract = new ethers.Contract(
    NFT_MULTISEND_ADDRESS,
    NFTMultisendAbi,
    await web3Provider.getSigner()
  );
  let errorTokenIds = await ERC721TokenVerify(
    senderAddress,
    tokenIds,
    tokenContract
  );
  if (errorTokenIds.length > 0)
    return { type: 'INVALID_TOKENS', errorTokenIds };
  else {
    let feeETH = await NFTMultisendContract.feeETH();
    let tx = await NFTMultisendContract.multiSendERC721ETH(
      tokenContract.address,
      receivingAddresses,
      tokenIds,
      {
        value: ethers.utils.parseEther(formatUnits(feeETH)),
      }
    );
    //await tokenContract.setApprovalForAll(NFT_MULTISEND_ADDRESS, false);
    return { type: 'SUCCESS', tx };
  }
};

export const multiSendERC721ZUT = async (
  web3Provider,
  tokenContract,
  receivingAddresses,
  senderAddress,
  tokenIds
) => {
  const zutContract = new ethers.Contract(
    TOKEN_ADDRESSES.ZUT,
    zutAbi,
    await web3Provider.getSigner()
  );
  let NFTMultisendContract = new ethers.Contract(
    NFT_MULTISEND_ADDRESS,
    NFTMultisendAbi,
    await web3Provider.getSigner()
  );
  let errorTokenIds = ERC721TokenVerify(senderAddress, tokenIds, tokenContract);
  if (errorTokenIds.length > 0)
    return { type: 'INVALID_TOKENS', errorTokenIds };
  else {
    let isContractApproved = await tokenContract.isApprovedForAll(
      senderAddress,
      NFT_MULTISEND_ADDRESS
    );
    if (!isContractApproved) {
      await tokenContract.setApprovalForAll(NFT_MULTISEND_ADDRESS, true);
    }
    let ZUTTotalSupply = await tokenContract.totalSupply();
    await zutContract.approve(NFT_MULTISEND_ADDRESS, ZUTTotalSupply);
    let tx = await NFTMultisendContract.multiSendERC721ZUT(
      tokenContract.address,
      receivingAddresses,
      tokenIds
    );
    //await tokenContract.setApprovalForAll(NFT_MULTISEND_ADDRESS, false);
    return { type: 'SUCCESS', tx };
  }
};

export const multiSendERC1155ETH = async (
  web3Provider,
  tokenContract,
  receivingAddresses,
  senderAddress,
  tokenIds,
  tokenAmounts
) => {
  let NFTMultisendContract = new ethers.Contract(
    NFT_MULTISEND_ADDRESS,
    NFTMultisendAbi,
    await web3Provider.getSigner()
  );
  let errorTokenIds = await ERC1155TokenVerify(
    senderAddress,
    tokenIds,
    tokenAmounts,
    tokenContract
  );

  if (errorTokenIds.length > 0)
    return { type: 'INVALID_TOKENS', errorTokenIds };
  else {
    let feeETH = await NFTMultisendContract.feeETH();
    console.log('fee ETH', feeETH)

    let tx = await NFTMultisendContract.multiSendERC1155ETH(
      tokenContract.address,
      receivingAddresses,
      tokenIds.map((tokenId) => parseInt(tokenId)),
      tokenAmounts.map((tokenAmount) => parseInt(tokenAmount)),
      {
        value: ethers.utils.parseEther(formatUnits(feeETH)),
      }
    );
    //await tokenContract.setApprovalForAll(NFT_MULTISEND_ADDRESS, false);
    return { type: 'SUCCESS', tx };
  }
};

export const multiSendERC1155ZUT = async (
  web3Provider,
  tokenContract,
  receivingAddresses,
  senderAddress,
  tokenIds,
  tokenAmounts
) => {
  const zutContract = new ethers.Contract(
    TOKEN_ADDRESSES.ZUT,
    zutAbi,
    await web3Provider.getSigner()
  );
  let NFTMultisendContract = new ethers.Contract(
    NFT_MULTISEND_ADDRESS,
    NFTMultisendAbi,
    await web3Provider.getSigner()
  );
  let errorTokenIds = ERC1155TokenVerify(
    senderAddress,
    tokenIds,
    tokenAmounts,
    tokenContract
  );
  if (errorTokenIds.length > 0)
    return { type: 'INVALID_TOKENS', errorTokenIds };
  else {
    await tokenContract.setApprovalForAll(NFT_MULTISEND_ADDRESS, true);
    let ZUTTotalSupply = await tokenContract.totalSupply();
    await zutContract.approve(NFT_MULTISEND_ADDRESS, ZUTTotalSupply);

    let tx = await NFTMultisendContract.multiSendERC1155ZUT(
      tokenContract.address,
      receivingAddresses,
      tokenIds
    );
    return { type: 'SUCCESS', tx };
  }
};

export const multiSendERC20ETH = async (
  web3Provider,
  tokenContract,
  receivingAddresses,
  senderAddress,
  tokenAmounts
) => {
  let NFTMultisendContract = new ethers.Contract(
    NFT_MULTISEND_ADDRESS,
    NFTMultisendAbi,
    await web3Provider.getSigner()
  );

  let senderBalance = await tokenContract.balanceOf(senderAddress);
  let totalSendAmount = BigNumber.from('0');
  tokenAmounts.forEach((tokenAmount) => {
    totalSendAmount = totalSendAmount.add(ethToWei(tokenAmount));
  });

  console.log('totalSendAmount', totalSendAmount.toString());
  console.log('senderBalance', senderBalance.toString());
  console.log(
    'totalSendAmount.gt(senderBalance)',
    totalSendAmount.gt(senderBalance)
  );

  if (totalSendAmount.gt(senderBalance))
    return { type: 'INSUFFICIENT_BALANCE' };
  else {
    let feeETH = await NFTMultisendContract.feeETH();

    let tx = await NFTMultisendContract.multiSendERC20ETH(
      tokenContract.address,
      receivingAddresses,
      tokenAmounts.map((tokenAmount) => BigNumber.from(ethToWei(tokenAmount))),
      {
        value: ethers.utils.parseEther(formatUnits(feeETH)),
      }
    );
    return { type: 'SUCCESS', tx };
  }
};

export const multiSendERC20ZUT = async (
  web3Provider,
  tokenContract,
  receivingAddresses,
  senderAddress,
  tokenAmounts
) => {
  const zutContract = new ethers.Contract(
    TOKEN_ADDRESSES.ZUT,
    zutAbi,
    await web3Provider.getSigner()
  );
  let NFTMultisendContract = new ethers.Contract(
    NFT_MULTISEND_ADDRESS,
    NFTMultisendAbi,
    await web3Provider.getSigner()
  );
  let senderBalance = await tokenContract.balanceOf(senderAddress);
  let totalSendAmount = BigNumber.from('0');
  tokenAmounts.forEach((tokenAmount) => {
    totalSendAmount = totalSendAmount.add(ethToWei(tokenAmount));
  });

  console.log('totalSendAmount', totalSendAmount.toString());
  console.log('senderBalance', senderBalance.toString());
  console.log(
    'totalSendAmount.gt(senderBalance)',
    totalSendAmount.gt(senderBalance)
  );

  if (totalSendAmount.gt(senderBalance)) {
    return { type: 'INSUFFICIENT_BALANCE' };
  } else {
    let totalSupply = await zutContract.totalSupply();
    await zutContract.approve(NFT_MULTISEND_ADDRESS, totalSupply);
    console.log('ZUT allowance', totalSendAmount.toString());

    let tx = await NFTMultisendContract.multiSendERC20ZUT(
      tokenContract.address,
      receivingAddresses,
      tokenAmounts.map((tokenAmount) => BigNumber.from(ethToWei(tokenAmount)))
    );
    console.log('5');

    return { type: 'SUCCESS', tx };
  }
};

export const getERC1155TokenUri = async (web3Provider, tokenAddress) => {
  const ERC1155Contract = new ethers.Contract(
    tokenAddress,
    IERC1155Abi,
    await web3Provider.getSigner()
  );
  const uri = await ERC1155Contract.uri(0);
  return uri;
};

export const adminWithdrawETH = async (web3Provider) => {
  let NFTMultisendContract = new ethers.Contract(
    NFT_MULTISEND_ADDRESS,
    NFTMultisendAbi,
    await web3Provider.getSigner()
  );

  return await NFTMultisendContract.withdrawEth();
};

export const adminWithdrawZUT = async (web3Provider) => {
  let NFTMultisendContract = new ethers.Contract(
    NFT_MULTISEND_ADDRESS,
    NFTMultisendAbi,
    await web3Provider.getSigner()
  );

  return await NFTMultisendContract.withdrawZut();
};

export const getNFTMultisendETHBalance = async (web3Provider) => {
  return getAccountEthBalance(web3Provider, NFT_MULTISEND_ADDRESS);
};

export const getNFTMultisendZUTBalance = async (web3Provider) => {
  return getAccountZutBalance(web3Provider, NFT_MULTISEND_ADDRESS);
};

export const getNFTMultisendETHFees = async (web3Provider) => {
  let NFTMultisendContract = new ethers.Contract(
    NFT_MULTISEND_ADDRESS,
    NFTMultisendAbi,
    await web3Provider.getSigner()
  );

  return await NFTMultisendContract.feeETH();
};

export const getNFTMultisendZUTFees = async (web3Provider) => {
  let NFTMultisendContract = new ethers.Contract(
    NFT_MULTISEND_ADDRESS,
    NFTMultisendAbi,
    await web3Provider.getSigner()
  );

  return await NFTMultisendContract.feeZUT();
};

export const setNFTMultisendETHFees = async (web3Provider, newFee) => {
  let NFTMultisendContract = new ethers.Contract(
    NFT_MULTISEND_ADDRESS,
    NFTMultisendAbi,
    await web3Provider.getSigner()
  );

  return await NFTMultisendContract.setFeeETH(ethToWei(newFee));
};

export const setNFTMultisendZUTFees = async (web3Provider, newFee) => {
  let NFTMultisendContract = new ethers.Contract(
    NFT_MULTISEND_ADDRESS,
    NFTMultisendAbi,
    await web3Provider.getSigner()
  );

  return await NFTMultisendContract.setFeeZUT(ethToWei(newFee));
};

export const queryAccountERC1155Tokens = async (
  tokenContract,
  accountAddress,
  startI,
  endI
) => {
  let accountAddresses = [];
  let tokenIds = [];
  for (let i = startI; i <= endI; i++) {
    accountAddresses.push(accountAddress);
    tokenIds.push(i);
  }

  let tokenBalances = await tokenContract.balanceOfBatch(
    accountAddresses,
    tokenIds
  );
  let ownedTokenBalances = [];
  for (let i = 0; i < tokenBalances.length; i++) {
    if (tokenBalances[i] > 0) {
      ownedTokenBalances.push({
        tokenId: i + startI,
        balance: tokenBalances[i],
      });
    }
  }

  return ownedTokenBalances;
};
